<template>
  <LoaderWrapper ref="loader" class="h-100">
    <div class="row m-4 mt-0 mb-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0">
          <div class="row">

            <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
              <div class="col p-0">
                <DatePicker v-model="requestDTO.fromDate" mode="dateTime" :model-modifiers="{string: true}" is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
                  <template v-slot="{ inputValue, togglePopover, inputEvents }">
                    <input
                      class="form-control form-control-sm"
                      :placeholder="helper.trans('labels.from_date')"
                      :value="inputValue"
                      @focus="togglePopover"
                      @change="inputEvents.change"
                    />
                  </template>
                </DatePicker>
              </div>
            </div>

            <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
              <div class="col p-0">
                <DatePicker v-model="requestDTO.toDate" mode="dateTime" :model-modifiers="{string: true}" is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
                  <template v-slot="{ inputValue, togglePopover, inputEvents }">
                    <input
                      class="form-control form-control-sm"
                      :placeholder="helper.trans('labels.to_date')"
                      :value="inputValue"
                      @focus="togglePopover"
                      @change="inputEvents.change"
                    />
                  </template>
                </DatePicker>
              </div>
            </div>

            <div class="form-group mb-2 col-md-3 col-lg-3 col-xl-2 me-2">
              <div class="col p-0">
                <AdminMultiselect :multiple="true" v-model="adminsFilter" />
              </div>
            </div>

            <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
              <div class="col p-0">
                <Multiselect
                  id="support-activity-directions"
                  class="multiselect-sm"
                  v-model="requestDTO.directions"
                  :options="Object.values(Direction)"
                  :custom-label="(opt) => helper.trans(`direction.${opt}`)"
                  :multiple="true"
                  select-label="Select"
                  :placeholder="helper.trans('labels.type')"
                />
              </div>
            </div>

            <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
              <div class="col p-0">
                <Multiselect
                  id="support-activity-currencies"
                  class="multiselect-sm multiselect-capitalize"
                  v-model="requestDTO.currencies"
                  :options="store.getters['currencyMethods/currencies']"
                  :custom-label="(opt) => opt"
                  :multiple="true"
                  select-label="Select"
                  :placeholder="helper.trans('labels.currency')"
                />
              </div>
            </div>

            <div class="form-group mb-2 col">
              <div class="col-auto p-0 text-end">
                <button type="button" @click.prevent="applyFilters()" class="btn btn-outline-primary btn-sm rounded-pill me-2 text-capitalize-first">{{ helper.trans('button.apply') }}</button>
                <button type="button" @click.prevent="clearFilters()" class="btn btn-outline-secondary btn-sm rounded-pill text-capitalize-first">{{ helper.trans('button.reset') }}</button>
              </div>
            </div>
          </div>

          <div class="row mt-2">
            <div class="form-group mb-2 col-sm-12">
              <div class="col p-0">
                <label class="me-3">{{ helper.trans('statistic.supportActivity.results_label') }}</label>
                <div class="form-check form-check-inline" v-for="result in Object.values(SupportActivityResult)" :key="result">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="`support-activity-result-${result}`"
                    :checked="requestDTO.results.includes(result)"
                    :disabled="isOnlyPickedResult(result)"
                    @change="toggleResult(result)"
                  />
                  <label class="form-check-label" :for="`support-activity-result-${result}`">
                    {{ helper.trans(`statistic.supportActivity.filter_result.${result}`) }}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="row mt-2">
            <div class="form-group mb-2 col-sm-6">
              <div class="col p-0">
                <template v-for="mode in ['chart', 'table']" :key="mode">
                  <input type="radio" v-model="viewMode" :value="mode" class="btn-check" name="support-activity-view" :id="`support-activity-view-${mode}`" autocomplete="off">
                  <label class="btn btn-sm btn-outline-dark me-2" :for="`support-activity-view-${mode}`">{{ helper.trans(`statistic.supportActivity.view.${mode}`) }}</label>
                </template>
              </div>
            </div>
            <div class="col-sm-6 text-end">
              <button type="button" @click.prevent="downloadReport()" class="btn btn-outline-primary btn-sm rounded-pill text-capitalize-first">
                <i class="fa-solid fa-download"></i> {{ helper.trans('labels.download_report') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row m-4 mb-0 mt-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0">

          <div v-if="!isLoaded" class="text-center text-muted p-4">
            {{ helper.trans('statistic.supportActivity.prompt') }}
          </div>

          <div v-else-if="isEmpty" class="text-center text-muted p-4">
            <div>{{ helper.trans('statistic.supportActivity.empty') }}</div>
            <small>{{ helper.trans('statistic.supportActivity.retention_hint') }}</small>
          </div>

          <div v-else-if="viewMode === 'chart'">
            <Bar
              :chart-options="chartOptions"
              :chart-data="chartData"
            />
          </div>

          <div v-else class="overflow-auto">
            <table class="table table-bordered align-middle text-left text-nowrap position-relative">
              <thead>
                <tr class="bg-light">
                  <th v-for="column in columns" :key="column.field" class="text-capitalize-first" role="button" @click="sortBy(column.field)">
                    {{ helper.trans(column.title) }}
                    <i v-if="sortField === column.field" class="fa-solid" :class="sortDesc ? 'fa-caret-down' : 'fa-caret-up'"></i>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in sortedItems" :key="`${item.bucket}-${item.adminId}-${item.result}-${item.direction}-${item.currency}-${index}`">
                  <td>{{ dateHelper.format(item.bucket, bucketFormat) }}</td>
                  <td>
                    {{ item.adminName }}
                    <div><small class="text-muted">{{ item.adminEmail }}</small></div>
                  </td>
                  <td>{{ helper.trans(`user_roles.${item.adminRole}`) }}</td>
                  <td>{{ helper.trans(`statistic.supportActivity.result.${item.result}`) }}</td>
                  <td>{{ helper.trans(`direction.${item.direction}`) }}</td>
                  <td>{{ item.currency }}</td>
                  <td class="text-end">{{ numberFormatter.formatNumber(item.count) }}</td>
                  <td class="text-end">{{ numberFormatter.formatNumber(parseFloat(item.takerSum ?? '0'), 2) }} {{ item.currency }}</td>
                  <td class="text-end">{{ numberFormatter.formatNumber(parseFloat(item.makerSum ?? '0'), 2) }} {{ item.makerCurrency }}</td>
                  <td class="text-end">
                    {{ helper.formatDuration(item.reactionSecondsAvg) }} /
                    {{ helper.formatDuration(item.reactionSecondsMin) }} /
                    {{ helper.formatDuration(item.reactionSecondsMax) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </div>
  </LoaderWrapper>
</template>
<script lang="ts">
import {computed, defineComponent, onUnmounted, reactive, ref, toRefs} from 'vue'
import {useStore} from "vuex";
import {useNotification} from "@kyvg/vue3-notification";
import {DatePicker} from "v-calendar";
import Multiselect from "vue-multiselect";
import moment from "moment";
import {Bar} from "vue-chartjs";
import {Chart as ChartJS, Title, Tooltip, Legend, BarElement, PointElement, CategoryScale, LinearScale} from 'chart.js'
import {Chart, ChartOptions} from "chart.js";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import AdminMultiselect from "@/components/AdminMultiselect.vue";
import APIClient from "@/service/APIClient";
import {helper} from "@/service/CommonHelper";
import {dateHelper} from "@/service/DateHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import {Direction} from "@/models/enum/Direction";
import {SupportActivityResult} from "@/models/enum/SupportActivityResult";
import {StatisticGranularity} from "@/models/enum/StatisticGranularity";
import SupportActivityItemDTO from "@/models/SupportActivityItemDTO";
import SupportActivityStatisticDTO from "@/models/SupportActivityStatisticDTO";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";
import UserDTO from "@/models/UserDTO";

const api = new APIClient();
const notification = useNotification();

ChartJS.register(Title, Tooltip, Legend, BarElement, PointElement, CategoryScale, LinearScale)
ChartJS.defaults.color = "#c0ccda";

const TOOLTIP_ID = 'supportActivityChartTooltip';
const TOOLTIP_MAX_WIDTH = 420;
const TOOLTIP_VIEWPORT_MARGIN = 8;
const TOOLTIP_CARET_OFFSET = 12;

const RESULT_COLORS: Record<string, string> = {
  [SupportActivityResult.COMPLETED]: '#2bc568',
  [SupportActivityResult.CANCELED]: '#e93939',
};

const RESULT_ORDER = [SupportActivityResult.COMPLETED, SupportActivityResult.CANCELED] as string[];

// granularity comes from the backend, it only drives the label format
const AXIS_FORMATS: Record<string, string> = {
  [StatisticGranularity.HOUR]: 'DD-MM HH:mm',
  [StatisticGranularity.DAY]: 'DD-MM',
  [StatisticGranularity.WEEK]: 'DD-MM',
  [StatisticGranularity.MONTH]: 'MM-YYYY',
};

const BUCKET_FORMATS: Record<string, string> = {
  [StatisticGranularity.HOUR]: 'YYYY-MM-DD HH:mm',
  [StatisticGranularity.DAY]: 'YYYY-MM-DD',
  [StatisticGranularity.WEEK]: 'YYYY-MM-DD',
  [StatisticGranularity.MONTH]: 'MM-YYYY',
};

const COLUMNS = [
  {field: 'bucket', title: 'statistic.supportActivity.column.period'},
  {field: 'adminName', title: 'statistic.supportActivity.column.admin'},
  {field: 'adminRole', title: 'statistic.supportActivity.column.role'},
  {field: 'result', title: 'statistic.supportActivity.column.result'},
  {field: 'direction', title: 'statistic.supportActivity.column.direction'},
  {field: 'currency', title: 'labels.currency'},
  {field: 'count', title: 'statistic.supportActivity.column.count'},
  {field: 'takerSum', title: 'statistic.supportActivity.column.taker_sum'},
  {field: 'makerSum', title: 'statistic.supportActivity.column.maker_sum'},
  {field: 'reactionSecondsAvg', title: 'statistic.supportActivity.column.reaction'},
];

const NUMERIC_FIELDS = ['count', 'takerSum', 'makerSum', 'reactionSecondsAvg'];

/**
 * Lightens (amount > 0) or darkens (amount < 0) a hex color, so that several
 * admins can be told apart while staying inside the green/red result palette.
 */
function shadeColor(hex: string, amount: number): string {
  const num = parseInt(hex.slice(1), 16);
  const channels = [(num >> 16) & 255, (num >> 8) & 255, num & 255];

  return '#' + channels
    .map((channel) => amount >= 0
      ? Math.round(channel + (255 - channel) * amount)
      : Math.round(channel * (1 + amount)))
    .map((channel) => Math.max(0, Math.min(255, channel)).toString(16).padStart(2, '0'))
    .join('');
}

function adminShade(index: number): number {
  if (index <= 0) {
    return 0;
  }
  const step = Math.min(0.6, 0.18 * Math.ceil(index / 2));

  return index % 2 === 1 ? step : -step;
}

interface SeriesDescriptor {
  key: string
  adminId: number
  adminName: string
  result: string
}

interface BucketMeta {
  count: number
  takerSums: Record<string, number>
  makerSums: Record<string, number>
  reactionWeighted: number
  reactionWeight: number
  reactionMin: number|null
  reactionMax: number|null
}

function emptyMeta(): BucketMeta {
  return {
    count: 0,
    takerSums: {},
    makerSums: {},
    reactionWeighted: 0,
    reactionWeight: 0,
    reactionMin: null,
    reactionMax: null,
  };
}

function mergeMeta(target: BucketMeta, source: BucketMeta): BucketMeta {
  target.count += source.count;
  for (const currency of Object.keys(source.takerSums)) {
    target.takerSums[currency] = (target.takerSums[currency] ?? 0) + source.takerSums[currency];
  }
  for (const currency of Object.keys(source.makerSums)) {
    target.makerSums[currency] = (target.makerSums[currency] ?? 0) + source.makerSums[currency];
  }
  target.reactionWeighted += source.reactionWeighted;
  target.reactionWeight += source.reactionWeight;
  if (source.reactionMin !== null) {
    target.reactionMin = target.reactionMin === null ? source.reactionMin : Math.min(target.reactionMin, source.reactionMin);
  }
  if (source.reactionMax !== null) {
    target.reactionMax = target.reactionMax === null ? source.reactionMax : Math.max(target.reactionMax, source.reactionMax);
  }

  return target;
}

function formatSums(sums: Record<string, number>): string[] {
  return Object.keys(sums)
    .sort()
    .filter((currency) => sums[currency] !== 0)
    .map((currency) => `${numberFormatter.formatNumber(sums[currency], 2)} ${currency}`);
}

function defaultRequestDTO() {
  return {
    fromDate: moment().subtract(7, 'days').format('YYYY-MM-DD HH:mm:ss'),
    toDate: moment().format('YYYY-MM-DD HH:mm:ss'),
    results: [SupportActivityResult.COMPLETED, SupportActivityResult.CANCELED] as string[],
    directions: [] as string[],
    currencies: [] as string[],
  };
}

export default defineComponent({
  components: {
    LoaderWrapper,
    AdminMultiselect,
    DatePicker,
    Multiselect,
    Bar,
  },
  setup() {
    const store = useStore();
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null);

    const state = reactive({
      adminsFilter: [] as UserDTO[],
      requestDTO: defaultRequestDTO(),
      response: null as SupportActivityStatisticDTO | null,
      viewMode: 'chart' as string,
      sortField: 'bucket' as string,
      sortDesc: false as boolean,
    });

    const granularity = computed(() => state.response?.granularity ?? StatisticGranularity.DAY);
    const axisFormat = computed(() => AXIS_FORMATS[granularity.value] ?? 'DD-MM');
    const bucketFormat = computed(() => BUCKET_FORMATS[granularity.value] ?? 'YYYY-MM-DD');
    const items = computed<SupportActivityItemDTO[]>(() => state.response?.items ?? []);
    const isLoaded = computed(() => state.response !== null);
    const isEmpty = computed(() => isLoaded.value && items.value.length === 0);

    /**
     * The endpoint returns a long format: one row per
     * (bucket, admin, result, direction, currency). The chart needs one series
     * per (admin, result), with counts summed over directions and currencies
     * and missing buckets filled with zeros.
     */
    const pivot = computed(() => {
      const labels = Array.from(new Set(items.value.map((item) => item.bucket)))
        .sort((a, b) => moment(a).valueOf() - moment(b).valueOf());

      const seriesInfo = {} as Record<string, SeriesDescriptor>;
      const meta = {} as Record<string, BucketMeta>;

      for (const item of items.value) {
        const key = `${item.adminId}|${item.result}`;
        if (!seriesInfo[key]) {
          seriesInfo[key] = {key, adminId: item.adminId, adminName: item.adminName, result: item.result};
        }

        const metaKey = `${key}|${item.bucket}`;
        const cell = meta[metaKey] ?? (meta[metaKey] = emptyMeta());

        cell.count += item.count;
        cell.takerSums[item.currency] = (cell.takerSums[item.currency] ?? 0) + parseFloat(item.takerSum ?? '0');
        if (item.makerCurrency) {
          cell.makerSums[item.makerCurrency] = (cell.makerSums[item.makerCurrency] ?? 0) + parseFloat(item.makerSum ?? '0');
        }
        if (item.reactionSecondsAvg !== null && item.count > 0) {
          cell.reactionWeighted += item.reactionSecondsAvg * item.count;
          cell.reactionWeight += item.count;
        }
        if (item.reactionSecondsMin !== null) {
          cell.reactionMin = cell.reactionMin === null ? item.reactionSecondsMin : Math.min(cell.reactionMin, item.reactionSecondsMin);
        }
        if (item.reactionSecondsMax !== null) {
          cell.reactionMax = cell.reactionMax === null ? item.reactionSecondsMax : Math.max(cell.reactionMax, item.reactionSecondsMax);
        }
      }

      const series = Object.values(seriesInfo).sort((a, b) => {
        const byName = a.adminName.localeCompare(b.adminName);

        return byName !== 0 ? byName : RESULT_ORDER.indexOf(a.result) - RESULT_ORDER.indexOf(b.result);
      });

      const adminIds = Array.from(new Set(series.map((s) => s.adminId)));

      const datasets = series.map((s) => ({
        label: `${s.adminName} — ${helper.trans(`statistic.supportActivity.result.${s.result}`)}`,
        backgroundColor: shadeColor(RESULT_COLORS[s.result] ?? '#4e8ae5', adminShade(adminIds.indexOf(s.adminId))),
        borderRadius: {topLeft: 6, topRight: 6, bottomLeft: 6, bottomRight: 6},
        borderSkipped: false,
        stack: `admin-${s.adminId}`,
        data: labels.map((bucket) => meta[`${s.key}|${bucket}`]?.count ?? 0),
      }));

      return {labels, datasets, series, meta, adminIds};
    });

    const chartData = computed(() => ({
      labels: pivot.value.labels,
      datasets: pivot.value.datasets,
    }));

    const appendRow = (tableBody: HTMLElement, text: string, color: string|null = null) => {
      const tr = document.createElement('tr');
      tr.style.backgroundColor = 'inherit';
      tr.style.borderWidth = '0';

      const td = document.createElement('td');
      td.style.borderWidth = '0';

      if (color) {
        const span = document.createElement('span');
        span.style.background = color;
        span.style.borderWidth = '2px';
        span.style.marginRight = '10px';
        span.style.height = '10px';
        span.style.width = '10px';
        span.style.display = 'inline-block';
        td.appendChild(span);
      }

      td.appendChild(document.createTextNode(text));
      tr.appendChild(td);
      tableBody.appendChild(tr);
    };

    const externalTooltipHandler = (context: any) => {
      const chart = context.chart as Chart;
      const tooltip = context.tooltip;

      let tooltipEl = document.querySelector(`#${TOOLTIP_ID}`) as HTMLDivElement;

      if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.id = TOOLTIP_ID;
        tooltipEl.style.background = 'rgba(0, 0, 0, 0.7)';
        tooltipEl.style.borderRadius = '5px';
        tooltipEl.style.color = 'white';
        tooltipEl.style.opacity = '1';
        tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.transition = 'opacity .1s ease';
        tooltipEl.style.whiteSpace = 'normal';

        const table = document.createElement('table');
        table.style.margin = '0px';
        table.style.fontSize = '85%';
        table.style.lineHeight = '16px';
        table.style.wordBreak = 'break-word';

        tooltipEl.appendChild(table);
        document.body.appendChild(tooltipEl);
      }

      const index = tooltip.dataPoints?.[0]?.dataIndex;
      if (tooltip.opacity === 0 || index === undefined) {
        tooltipEl.style.opacity = '0';
        return;
      }

      const bucket = pivot.value.labels[index];
      const series = pivot.value.series;
      const meta = pivot.value.meta;

      const tableHead = document.createElement('thead');
      const headTr = document.createElement('tr');
      headTr.style.borderWidth = '0';
      const th = document.createElement('th');
      th.style.borderWidth = '0';
      th.appendChild(document.createTextNode(dateHelper.format(bucket, bucketFormat.value)));
      headTr.appendChild(th);
      tableHead.appendChild(headTr);

      const tableBody = document.createElement('tbody');

      // dataPoints excludes datasets hidden through the legend
      const adminIds = [] as number[];
      for (const point of (tooltip.dataPoints ?? [])) {
        const descriptor = series[point.datasetIndex];
        if (descriptor && adminIds.indexOf(descriptor.adminId) === -1) {
          adminIds.push(descriptor.adminId);
        }
      }

      for (const adminId of adminIds) {
        const adminSeries = series.filter((s) => s.adminId === adminId);
        const totals = emptyMeta();
        const countByResult = {} as Record<string, number>;

        for (const descriptor of adminSeries) {
          const cell = meta[`${descriptor.key}|${bucket}`];
          if (!cell) {
            continue;
          }
          countByResult[descriptor.result] = (countByResult[descriptor.result] ?? 0) + cell.count;
          mergeMeta(totals, cell);
        }

        if (totals.count === 0) {
          continue;
        }

        const counts = RESULT_ORDER
          .map((result) => `${helper.trans(`statistic.supportActivity.result.${result}`)}: ${countByResult[result] ?? 0}`)
          .join(' / ');

        appendRow(
          tableBody,
          `${adminSeries[0].adminName} — ${counts}`,
          shadeColor(RESULT_COLORS[SupportActivityResult.COMPLETED], adminShade(pivot.value.adminIds.indexOf(adminId))),
        );

        const sums = formatSums(totals.takerSums).concat(formatSums(totals.makerSums));
        if (sums.length) {
          appendRow(tableBody, `${helper.trans('statistic.supportActivity.tooltip.sums')}: ${sums.join(', ')}`);
        }

        const avg = totals.reactionWeight > 0 ? totals.reactionWeighted / totals.reactionWeight : null;
        appendRow(
          tableBody,
          `${helper.trans('statistic.supportActivity.tooltip.reaction')}: `
          + `${helper.formatDuration(avg)} / ${helper.formatDuration(totals.reactionMin)} / ${helper.formatDuration(totals.reactionMax)}`,
        );
      }

      const tableRoot = tooltipEl.querySelector('table') as HTMLTableElement;
      while (tableRoot.firstChild) {
        tableRoot.firstChild.remove();
      }
      tableRoot.appendChild(tableHead);
      tableRoot.appendChild(tableBody);

      tooltipEl.style.opacity = '1';
      tooltipEl.style.font = tooltip.options.bodyFont.string;
      tooltipEl.style.padding = tooltip.options.padding + 'px ' + tooltip.options.padding + 'px';

      // offsetWidth/offsetHeight are only meaningful once the content, the font
      // and the width cap are applied, so position last and keep the box inside
      // the viewport — the sums line can list several currencies and would
      // otherwise stretch past the screen edge
      const clientRect = chart.canvas.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      tooltipEl.style.maxWidth = Math.min(TOOLTIP_MAX_WIDTH, viewportWidth - TOOLTIP_VIEWPORT_MARGIN * 2) + 'px';

      const width = tooltipEl.offsetWidth;
      const height = tooltipEl.offsetHeight;

      const minLeft = window.scrollX + TOOLTIP_VIEWPORT_MARGIN;
      const maxLeft = window.scrollX + viewportWidth - width - TOOLTIP_VIEWPORT_MARGIN;
      const left = clientRect.left + window.scrollX + tooltip.caretX - width / 2;

      const minTop = window.scrollY + TOOLTIP_VIEWPORT_MARGIN;
      const maxTop = window.scrollY + document.documentElement.clientHeight - height - TOOLTIP_VIEWPORT_MARGIN;
      const caretTop = clientRect.top + window.scrollY + tooltip.caretY;
      // above the cursor by default, below it when there is no room above
      const top = caretTop - height - TOOLTIP_CARET_OFFSET >= minTop
        ? caretTop - height - TOOLTIP_CARET_OFFSET
        : caretTop + TOOLTIP_CARET_OFFSET;

      tooltipEl.style.left = Math.round(Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft))) + 'px';
      tooltipEl.style.top = Math.round(Math.min(Math.max(top, minTop), Math.max(minTop, maxTop))) + 'px';
    };

    const chartOptions = computed(() => {
      const format = axisFormat.value;

      return {
        animation: {
          duration: 0
        },
        responsive: true,
        aspectRatio: 16 / 8,
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
          },
          tooltip: {
            enabled: false,
            external: externalTooltipHandler,
          },
        },
        interaction: {
          intersect: false,
          mode: 'index',
        },
        scales: {
          x: {
            stacked: true,
            ticks: {
              callback: function (value: number): string {
                return dateHelper.format(this.getLabelForValue(value), format);
              }
            },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            ticks: {
              precision: 0,
            },
          }
        }
      } as ChartOptions;
    });

    const sortedItems = computed<SupportActivityItemDTO[]>(() => {
      const field = state.sortField;
      const direction = state.sortDesc ? -1 : 1;

      return [...items.value].sort((a, b) => {
        const left = (a as any)[field];
        const right = (b as any)[field];

        if (field === 'bucket') {
          return (moment(left).valueOf() - moment(right).valueOf()) * direction;
        }

        if (NUMERIC_FIELDS.indexOf(field) !== -1) {
          return ((parseFloat(left ?? '0') || 0) - (parseFloat(right ?? '0') || 0)) * direction;
        }

        return String(left ?? '').localeCompare(String(right ?? '')) * direction;
      });
    });

    const sortBy = (field: string) => {
      if (state.sortField === field) {
        state.sortDesc = !state.sortDesc;
        return;
      }
      state.sortField = field;
      state.sortDesc = false;
    };

    const isOnlyPickedResult = (result: string) => {
      return state.requestDTO.results.length === 1 && state.requestDTO.results[0] === result;
    };

    const toggleResult = (result: string) => {
      const index = state.requestDTO.results.indexOf(result);
      if (index === -1) {
        state.requestDTO.results.push(result);
        return;
      }
      // the backend treats an empty results[] as "both", so never let the UI
      // show "nothing selected" while the data says otherwise
      if (state.requestDTO.results.length > 1) {
        state.requestDTO.results.splice(index, 1);
      }
    };

    const buildParams = () => {
      const adminIds = state.adminsFilter.map((admin) => admin.id);
      const params: Record<string, unknown> = {
        fromDate: dateHelper.toUTC(state.requestDTO.fromDate),
        toDate: dateHelper.toUTC(state.requestDTO.toDate),
      };
      if (adminIds.length) params.adminIds = adminIds;
      if (state.requestDTO.results.length) params.results = state.requestDTO.results;
      if (state.requestDTO.directions.length) params.directions = state.requestDTO.directions;
      if (state.requestDTO.currencies.length) params.currencies = state.requestDTO.currencies;

      return params;
    };

    const notifyError = async (error: any) => {
      let data = error?.response?.data;

      // export requests use responseType blob, so a 400 body arrives as a Blob
      if (data instanceof Blob) {
        try {
          data = JSON.parse(await data.text());
        } catch (e) {
          data = null;
        }
      }

      let text = error instanceof Error ? error.message : String(error);
      if (Array.isArray(data) && data.length) {
        text = (data as ValidationErrorDTO[])
          .map((item) => helper.trans(`server_errors.${item.code}`, {}, item.message))
          .join('\n');
      }

      notification.notify({
        type: "error",
        title: helper.trans("labels.error"),
        text,
      });
    };

    const load = async () => {
      loader.value?.show();
      try {
        state.response = await api.call.statistic.supportActivity(buildParams());
      } catch (error) {
        state.response = null;
        await notifyError(error);
      } finally {
        loader.value?.hide();
      }
    };

    const applyFilters = () => {
      load();
    };

    const clearFilters = () => {
      state.adminsFilter = [];
      state.requestDTO = defaultRequestDTO();
      state.response = null;
    };

    // mirrors the backend Content-Disposition template, used when the header
    // is not exposed to the browser
    const fallbackExportFilename = () => {
      return `${helper.trans('statistic.supportActivity.export_filename')}_${moment().format('YYYY-MM-DD_HH-mm')}.xlsx`;
    };

    const downloadReport = async () => {
      loader.value?.show();
      try {
        const {blob, filename} = await api.exportWithFilename('support_activity_stat', buildParams(), fallbackExportFilename());
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        await notifyError(error);
      } finally {
        loader.value?.hide();
      }
    };

    onUnmounted(() => {
      document.querySelector(`#${TOOLTIP_ID}`)?.remove();
    });

    return {
      ...toRefs(state),
      loader,
      store,
      helper,
      dateHelper,
      numberFormatter,
      Direction,
      SupportActivityResult,
      columns: COLUMNS,
      granularity,
      bucketFormat,
      isLoaded,
      isEmpty,
      chartData,
      chartOptions,
      sortedItems,
      sortBy,
      isOnlyPickedResult,
      toggleResult,
      applyFilters,
      clearFilters,
      downloadReport,
    };
  },
});
</script>
